import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PublishTemplateModal = ({ 
  isOpen, 
  onClose, 
  onPublish, 
  isPublishing,
  existingMetadata = null,
  // Props for preview video generation progress
  isGeneratingPreview = false,
  previewProgress = 0,
  previewStage = ''
}) => {
  const [formData, setFormData] = useState({
    description: '',
    category: 'other',
    tags: ''
  });

  const [errors, setErrors] = useState({});

  // Categories available for templates
  const categories = [
    { value: 'social', label: 'Social Media' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'presentation', label: 'Presentation' },
    { value: 'animation', label: 'Animation' },
    { value: 'other', label: 'Other' }
  ];

  // Load existing metadata if available
  useEffect(() => {
    if (existingMetadata) {
      setFormData(prev => ({
        ...prev,
        description: existingMetadata.description || '',
        category: existingMetadata.category || 'other',
        tags: Array.isArray(existingMetadata.tags) 
          ? existingMetadata.tags.join(', ') 
          : existingMetadata.tags || ''
      }));
    }
  }, [existingMetadata]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.tags.trim()) {
      newErrors.tags = 'At least one tag is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    // Convert tags string to array
    const tagsArray = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    onPublish({
      description: formData.description.trim(),
      category: formData.category,
      tags: tagsArray
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-ag-black-700 rounded-lg shadow-xl w-full max-w-md mx-4 border border-ag-black-400">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ag-black-400">
          <h2 className="text-xl font-bold text-white">
            {existingMetadata ? 'Update Template Metadata' : 'Publish as Template'}
          </h2>
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={isPublishing}
              rows={4}
              maxLength={500}
              placeholder="Describe this template..."
              className={`w-full px-3 py-2 bg-ag-black-800 border rounded-lg text-white placeholder-gray-400 
                focus:outline-none focus:ring-2 focus:ring-ag-green resize-none
                disabled:opacity-50 disabled:cursor-not-allowed
                ${errors.description ? 'border-red-400' : 'border-ag-black-400'}`}
            />
            <div className="flex justify-between mt-1">
              {errors.description && (
                <p className="text-sm text-red-400">{errors.description}</p>
              )}
              <p className="text-xs text-gray-400 ml-auto">
                {formData.description.length}/500
              </p>
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-white mb-2">
              Category <span className="text-red-400">*</span>
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              disabled={isPublishing}
              className={`w-full px-3 py-2 bg-ag-black-800 border rounded-lg text-white
                focus:outline-none focus:ring-2 focus:ring-ag-green
                disabled:opacity-50 disabled:cursor-not-allowed
                ${errors.category ? 'border-red-400' : 'border-ag-black-400'}`}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-sm text-red-400 mt-1">{errors.category}</p>
            )}
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-white mb-2">
              Tags <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              disabled={isPublishing || isGeneratingPreview}
              placeholder="e.g., social, video, promo (comma-separated)"
              className={`w-full px-3 py-2 bg-ag-black-800 border rounded-lg text-white placeholder-gray-400
                focus:outline-none focus:ring-2 focus:ring-ag-green
                disabled:opacity-50 disabled:cursor-not-allowed
                ${errors.tags ? 'border-red-400' : 'border-ag-black-400'}`}
            />
            {errors.tags && (
              <p className="text-sm text-red-400 mt-1">{errors.tags}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Separate multiple tags with commas
            </p>
          </div>

          {/* Preview Generation Progress */}
          {isGeneratingPreview && (
            <div className="border border-ag-black-400 rounded-lg p-4 bg-ag-black-800">
              <div className="flex items-center space-x-2 mb-2">
                <svg className="animate-spin h-4 w-4 text-ag-green" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-medium text-white">
                  {previewStage === 'initializing' && 'Initializing...'}
                  {previewStage === 'starting_export' && 'Starting export...'}
                  {previewStage === 'downloading_media' && 'Downloading media...'}
                  {previewStage === 'rendering' && 'Rendering frames...'}
                  {previewStage === 'encoding' && 'Encoding video...'}
                  {previewStage === 'finalizing' && 'Finalizing...'}
                  {previewStage === 'complete' && 'Complete!'}
                  {!['initializing', 'starting_export', 'downloading_media', 'rendering', 'encoding', 'finalizing', 'complete'].includes(previewStage) && 'Processing...'}
                </span>
              </div>
              <div className="w-full bg-ag-black-600 rounded-full h-2">
                <div 
                  className="bg-ag-green h-2 rounded-full transition-all duration-300"
                  style={{ width: `${previewProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {previewProgress}% complete
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isPublishing}
              className="px-4 py-2 text-sm font-medium text-white bg-ag-black-600 rounded-lg
                hover:bg-ag-black-500 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPublishing}
              className="px-4 py-2 text-sm font-medium text-black bg-ag-green rounded-lg
                hover:bg-green-400 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center space-x-2"
            >
              {isPublishing ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Publishing...</span>
                </>
              ) : (
                <span>{existingMetadata ? 'Update & Publish' : 'Publish Template'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublishTemplateModal;